import { useTranslation } from 'react-i18next';

export type ErrorMessageContext = Record<string, unknown>;

export function useErrorMessage(
	error: unknown,
	context: ErrorMessageContext = {}
): string | undefined {
	const { t, i18n } = useTranslation();

	if (!error) {
		return '';
	}

	function extractErrorMessage(err: unknown): string {
		if (typeof err === 'object' && err !== null) {
			if ('message' in err && typeof err.message === 'string') {
				return err.message;
			}
			for (const key in err) {
				if (Object.prototype.hasOwnProperty.call(err, key)) {
					const nestedMessage = extractErrorMessage(
						(err as Record<string, unknown>)[key]
					);
					if (nestedMessage) {
						return nestedMessage;
					}
				}
			}
		}
		return String(err);
	}

	let errorMessage = extractErrorMessage(error);

	/**
	 * To allow for translated error messages, schema error messages
	 * are allowed to be an i18n key.
	 * Example can be found at:
	 * https://github.com/BrandSourceDigital/hub/blob/1cce5382a93015a7e06685302e5901a4b1aa36d8/libs/data/dtos/src/util-structs.ts#L43
	 */
	if (errorMessage && i18n.exists(errorMessage)) {
		errorMessage = t(errorMessage, context);
	}

	// Return undefined instead of an empty string to make it more explicit that
	// there is no error message.
	return errorMessage || undefined;
}
